import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { navItemsForRole } from "./nav-items";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

export function Sidebar({ role }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const items = navItemsForRole(role, user);
  const initials = ((user?.full_name || user?.email || "?").split(/[\s@.]/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("")) || "?";

  return (
    <aside className="hidden h-full w-72 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
      {/* Logo — T-Mobile */}
      <div className="flex flex-col items-center gap-1 border-b border-slate-100 px-6 py-6">
        <img
          src="/tmobile-logo.png"
          alt="T-Mobile"
          className="h-8 w-auto object-contain"
        />
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Warehouse Inventory</p>
      </div>

      {/* User card */}
      {user && (
        <div className="border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-blue-700 text-sm font-semibold text-white">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">{user.full_name || "Dolphin User"}</p>
              <p className="truncate text-xs text-slate-500">{user.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* Nav — scrolls internally if too tall */}
      <div className="flex-1 overflow-y-auto px-3 py-5">
        <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Menu</p>
        <nav className="space-y-1">
          {items.map((item) => {
            const active = location.pathname === item.href || location.pathname.startsWith(item.href + "/");
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  active
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )}
              >
                <Icon className={cn("h-4 w-4 transition-colors", active ? "text-sky-300" : "text-slate-400 group-hover:text-slate-600")} />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Sign out — always pinned at bottom */}
      <div className="shrink-0 border-t border-slate-100 px-3 py-4">
        <button
          onClick={() => { logout(); navigate("/login"); }}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
        >
          <LogOut className="h-4 w-4 text-slate-400" />
          Sign Out
        </button>
        <p className="mt-3 px-3 text-[11px] leading-relaxed text-slate-400">
          Dolphin Digital OOH Media<br />© {new Date().getFullYear()} All rights reserved
        </p>
      </div>
    </aside>
  );
}
